import {
  InterviewSession,
  InterviewSessionActivityLog,
} from "../../../Interface/Index";
import aiInterviewService from "../../../Services/ai-interview.service";
import useInterviewRoundsHandlers from "./useInterviewRoundsHandlers";
import { useContext } from "react";
import LoaderContext from "../../../Context/LoaderContext/Index";

type ActivityLogResponseType = {
  isDisqualified: boolean;
  interviewSession: InterviewSession;
  jobApplication?: any;
} | null;

interface ProctoringHandlers {
  handleFullScreenExitAttempt: (
    session: InterviewSession
  ) => Promise<ActivityLogResponseType>;
  handleNotAllowedKeyPressing: (
    session: InterviewSession,
    keys: string[]
  ) => Promise<ActivityLogResponseType>;
  handleMultipleTabOpening: (
    session: InterviewSession
  ) => Promise<ActivityLogResponseType>;
  handleCursorFocusChange: (
    session: InterviewSession
  ) => Promise<ActivityLogResponseType>;
  handlePageRefesh: (
    session: InterviewSession
  ) => Promise<ActivityLogResponseType>;
}

const useProctoring = (): ProctoringHandlers => {
  const { handleUnqualifiedRound } = useInterviewRoundsHandlers();
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const logActivity = (
    session: InterviewSession,
    activity: InterviewSessionActivityLog
  ) => {
    if (!Array.isArray(session.activityLogs)) {
      session.activityLogs = [];
    }
    session.activityLogs.push(activity);
  };

  const handleResponse = async (response: ActivityLogResponseType) => {
    try {
      if (response?.isDisqualified && response?.jobApplication) {
        await handleUnqualifiedRound(
          response.jobApplication,
          response.interviewSession
        );
      }
      setLoading(false);
      setLoadingMessage(null);
      return response;
    } catch (error: any) {
      console.log("Error while handling activity log response", error);
      setLoading(false);
      setLoadingMessage(null);
      return null;
    }
  };

  const handleFullScreenExitAttempt = async (session: InterviewSession) => {
    setLoading(true);
    setLoadingMessage("Processing activity response...");
    const activity: InterviewSessionActivityLog = {
      event: "fullscreen_exit",
      timestamp: new Date(),
    };
    logActivity(session, activity);
    const response = await aiInterviewService.addActivityLogs(
      session._id,
      session
    );
    return handleResponse(response);
  };

  const handleNotAllowedKeyPressing = async (
    session: InterviewSession,
    keys: string[]
  ) => {
    setLoading(true);
    setLoadingMessage("Processing activity response...");
    const activity: InterviewSessionActivityLog = {
      event: "keyboard_shortcut",
      key: keys.join("+"),
      timestamp: new Date(),
    };
    logActivity(session, activity);
    const response = await aiInterviewService.addActivityLogs(
      session._id,
      session
    );
    return handleResponse(response);
  };

  const handleMultipleTabOpening = async (session: InterviewSession) => {
    setLoading(true);
    setLoadingMessage("Processing activity response...");
    const activity: InterviewSessionActivityLog = {
      event: "multiple_tabs",
      timestamp: new Date(),
    };
    logActivity(session, activity);
    const response = await aiInterviewService.addActivityLogs(
      session._id,
      session
    );
    return handleResponse(response);
  };

  const handleCursorFocusChange = async (session: InterviewSession) => {
    setLoading(true);
    setLoadingMessage("Processing activity response...");
    const activity: InterviewSessionActivityLog = {
      event: "cursor_focus_switch",
      timestamp: new Date(),
    };
    logActivity(session, activity);
    const response = await aiInterviewService.addActivityLogs(
      session._id,
      session
    );
    return handleResponse(response);
  };

  const handlePageRefesh = async (session: InterviewSession) => {
    setLoading(true);
    setLoadingMessage("Processing activity response...");
    const activity: InterviewSessionActivityLog = {
      event: "refresh_page",
      timestamp: new Date(),
    };
    logActivity(session, activity);
    const response = await aiInterviewService.addActivityLogs(
      session._id,
      session
    );
    return handleResponse(response);
  };

  return {
    handleFullScreenExitAttempt,
    handleNotAllowedKeyPressing,
    handleMultipleTabOpening,
    handleCursorFocusChange,
    handlePageRefesh,
  };
};

export default useProctoring;
